import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useTimestampService } from "../../hooks/useTimestampService";
import { RecordCard } from "../records/RecordCard";
import { EmptyState } from "../records/EmptyState";
import { ParsedRecord } from "../../types/records";
import { Timestamp } from "../../types/api";
import { LoadingSpinner } from "../LoadingSpinner";

interface RecentRecordsProps {
  format?: "list" | "grid";
  maxRecords?: number;
}

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1200px) {
    max-width: 100%;
  }
`;

const Records = styled.div<{ format?: "list" | "grid" }>`
  display: grid;
  grid-template-columns: ${({ format }) =>
    format === "list" ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))"};
  gap: 24px;
  margin: 32px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export const RecentRecords: React.FC<RecentRecordsProps> = ({
  format = "list",
}) => {
  const { timestamps, loading, error } = useTimestampService();
  const [parsedRecords, setParsedRecords] = useState<ParsedRecord[]>([]);
  const [blockchainData, setBlockchainData] = useState<{
    [key: string]: {
      index: number;
      hash: string;
      timestamp: string;
    };
  }>({});

  // Use a ref to track the current blockchainData without causing effect reruns
  const blockchainDataRef = useRef(blockchainData);

  // Keep the ref updated with the latest state
  useEffect(() => {
    blockchainDataRef.current = blockchainData;
  }, [blockchainData]);

  // Helper function to check if a string is valid JSON
  const isValidJSON = useCallback((str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  // Function to get timestamp data - wrapped in useCallback to prevent dependency changes
  const getTimestampData = useCallback(
    (timestamp: Timestamp) => {
      try {
        const decodedData = atob(timestamp.data);

        if (isValidJSON(decodedData)) {
          const data = JSON.parse(decodedData);
          return data;
        }
      } catch (error) {
        console.error("Error processing timestamp:", error);
      }

      return null;
    },
    [isValidJSON]
  );

  useEffect(() => {
    if (timestamps && timestamps.length > 0) {
      // Create a new blockchain data object instead of updating state in a loop
      const newBlockchainData = { ...blockchainDataRef.current };

      // Process all timestamps at once
      const records = timestamps
        .map((timestamp) => {
          const data = getTimestampData(timestamp);
          if (data) {
            // Collect blockchain data for batch update
            if (data.hash) {
              newBlockchainData[data.hash] = {
                index: timestamp.index,
                hash: timestamp.hash,
                timestamp: timestamp.timestamp,
              };
            }

            return {
              ...data,
              originalTimestamp: timestamp.timestamp,
              blockchainData: {
                index: timestamp.index,
                hash: timestamp.hash,
                timestamp: timestamp.timestamp,
              },
            };
          }
          return { error: "Failed to parse data" };
        })
        .filter((record) => !record.error);

      // Update state once with all changes
      setBlockchainData(newBlockchainData);
      setParsedRecords(records);
    }
  }, [timestamps, getTimestampData]);

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  // Format key for display
  const formatKey = (key: string) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
  };

  return loading ? (
    <Container>
      <LoadingSpinner />
    </Container>
  ) : (
    <Container>
      {parsedRecords.length === 0 || error ? (
        <EmptyState error={error} />
      ) : (
        <Records>
          {parsedRecords.map((record, index) => (
            <RecordCard
              key={index}
              record={record}
              index={index}
              formatDate={formatDate}
              formatKey={formatKey}
              showJsonLink={false}
              canBeExpanded={false}
              blockchainData={
                record.blockchainData ||
                (record.hash ? blockchainData[record.hash] : undefined)
              }
            />
          ))}
        </Records>
      )}
    </Container>
  );
};
